import React from "react";
import PropTypes from "prop-types";
import AIForm from "components/ai_tools/AIForm";
import ChatArea from "components/ai_tools/ChatArea";

import { ACCESS_TIERS } from "constants";
import { AccessTierProvider, useAccessTier } from "contexts/AccessTierContext";
import AIToolsLayout from "./layout/AIToolsLayout";
import {
  AI_TOOL_CHANNELS,
  AI_TOOL_NAMES,
  QUESTION_MINIMUM_LENGTH
} from "./constants";
import useChatMessages from "./hooks/useChatMessages";

const ContextualizedAskLitchartsAI = (props) => {
  return (
    <AccessTierProvider accessTier={props.accessTier}>
      <AskLitchartsAI {...props} />
    </AccessTierProvider>
  );
};

const AskLitchartsAI = (props) => {
  const {
    quotaRemaining: quotaFromProps,
    signupPath,
    submissionPath,
    aiAnswersCompletePath,
    infiniteAnswers
  } = props;
  const { isSignedOut } = useAccessTier();

  const {
    canFetchMoreAnswers,
    isStreaming,
    loadingPastMessages,
    quotaRemaining,
    messages,
    prevSlug,
    handleAskAIFormSubmit,
    fetchAiAnswers,
    canAskQuestions
  } = useChatMessages({
    aiAnswersCompletePath,
    channel: AI_TOOL_CHANNELS.ASK_AI,
    eventCategory: "Ask LitCharts AI",
    infiniteAnswers,
    locked: isSignedOut || quotaFromProps <= 0,
    quotaFromProps,
    signupPath,
    submissionPath,
    toolName: AI_TOOL_NAMES.ASK_AI,
    upgradeable: false
  });

  return (
    <AIToolsLayout>
      <AIForm
        {...props}
        canAskQuestions={canAskQuestions}
        loading={isStreaming || loadingPastMessages}
        onSubmit={handleAskAIFormSubmit}
        // Note, please read before removing.  This line causes a re-render that clears
        // out the "Question" field while preserving the "Title" field upon a
        // successful Ask Litcharts AI response.
        key={`quota-remaining-${quotaRemaining}`}
        quotaRemaining={quotaRemaining}
        questionMinimum={QUESTION_MINIMUM_LENGTH}
        values={{ title: prevSlug }}
      />
      <ChatArea
        canLoadMore={canFetchMoreAnswers}
        isStreaming={isStreaming}
        loadingPastMessages={loadingPastMessages}
        loadMore={fetchAiAnswers}
        messages={messages}
      />
    </AIToolsLayout>
  );
};

AskLitchartsAI.propTypes = {
  accessTier: PropTypes.oneOf(Object.values(ACCESS_TIERS)).isRequired,
  aiAnswersCompletePath: PropTypes.string,
  locked: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string
    })
  ),
  questionMinimum: PropTypes.number,
  quotaRemaining: PropTypes.number.isRequired,
  resetDate: PropTypes.string,
  signupPath: PropTypes.string,
  submissionPath: PropTypes.string
};

AskLitchartsAI.defaultProps = {
  aiAnswersCompletePath: "/ai_answers/complete",
  locked: false,
  options: [],
  questionMinimum: 25,
  resetDate: "",
  signupPath: "/sign-up?focus=teacher",
  submissionPath: "/ai/ask-litcharts-ai"
};

export default ContextualizedAskLitchartsAI;
